<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-composition-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form
        ref="compositionForm"
        @submit="addComposition"
        @invalid-submit="handleInvalidSubmit"
        :validation-schema="compositionSchema"
        :validateOnInput="false"
        :validateOnMount="false"
        :initialValues="{
          titre: '',
          dateComposition: getCurrentDate(),
          dateFinSoumission: getCurrentDate(),
          dureeMinutes: 15,
          nombreEssais: 1,
          professeur: professeurConnecteId,
          niveau: null,
          matiere: null,
          filiere: null,
          anneeAcademique: null,
          typeComposition: 'QCM',
          questions: [],
          questionsAdded: addedQuestions
        }"
      >

        <!-- Ligne 1 : Titre + Date composition + Date limite soumission -->
        <div class="row">
          <div class="col-md-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Titre <span class="text-danger">*</span>
              </label>
              <Field name="titre" type="text" v-model="titre"
                class="form-control shadow-none fs-md-15 text-black" placeholder="Entrer le titre"/>
              <ErrorMessage name="titre" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date de composition <span class="text-danger">*</span>
              </label>
              <Field name="dateComposition" type="date" :value="getCurrentDate()"
                class="form-control shadow-none fs-md-15 text-black"/>
              <ErrorMessage name="dateComposition" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-3">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Date limite de soumission <span class="text-danger">*</span>
              </label>
              <Field name="dateFinSoumission" type="date" :value="getCurrentDate()"
                class="form-control shadow-none fs-md-15 text-black"/>
              <ErrorMessage name="dateFinSoumission" class="text-danger"/>
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Durée (min) <span class="text-danger">*</span>
              </label>
              <Field name="dureeMinutes" type="number" min="1"
                class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 45"/>
              <ErrorMessage name="dureeMinutes" class="text-danger"/>
            </div>
          </div>
          <div class="col-md-1">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Essais <span class="text-danger">*</span>
              </label>
              <Field name="nombreEssais" type="number" min="1" max="3"
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Ex: 3"/>
              <small class="text-muted d-block mt-1">
                <i class="fa fa-info-circle me-1"></i>
                Max 3
              </small>
              <ErrorMessage name="nombreEssais" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Ligne 2 : Niveau + Filière + Matière -->
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field }">
                <Multiselect
                  :options="niveauxOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onNiveauChange"
                  placeholder="Sélectionner le niveau"
                />
              </Field>
              <ErrorMessage name="niveau" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Filière <span class="text-danger">*</span>
              </label>
              <Field name="filiere" v-slot="{ field }">
                <Multiselect
                  :options="filieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onFiliereChange"
                  placeholder="Sélectionner la filière"
                  :disabled="!selectedNiveau || isLoadingFilieres"
                />
              </Field>
              <ErrorMessage name="filiere" class="text-danger"/>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Matière <span class="text-danger">*</span>
              </label>
              <Field name="matiere" v-slot="{ field }">
                <Multiselect
                  :options="matieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-bind="field"
                  @change="onMatiereChange"
                  placeholder="Sélectionner la matière"
                  :disabled="!selectedNiveau || !selectedFiliere || isLoadingMatieres"
                />
              </Field>
              <ErrorMessage name="matiere" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Ligne 3 : Professeur + Année Académique + Type de composition (sur la même ligne) -->
        <div class="row">

          <!-- Professeur -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Professeur <span class="text-danger" v-if="!isProfesseurConnecte">*</span>
              </label>
              <div v-if="isProfesseurConnecte">
                <input 
                  type="text" 
                  :value="professeurConnecteNom" 
                  class="form-control shadow-none fs-md-15 text-black bg-light" 
                  disabled
                  readonly
                />
                <small class="text-muted">
                  <i class="fa fa-info-circle me-1"></i>
                  Vous créez cette composition en votre nom
                </small>
              </div>
              <Field v-else name="professeur" v-slot="{ field }">
                <Multiselect
                  :options="currentProfesseursOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-bind="field"
                  placeholder="Sélectionner le professeur"
                  :disabled="!selectedMatiere || !currentProfesseursOptions.length"
                />
              </Field>
              <ErrorMessage name="professeur" class="text-danger"/>
            </div>
          </div>

          <!-- Année Académique -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Année Académique <span class="text-danger">*</span>
              </label>
              <Field name="anneeAcademique" v-slot="{ field }">
                <Multiselect
                  :options="anneesAcademiquesOptions"
                  :searchable="true"
                  track-by="label"
                  label="label"
                  v-model="field.value"
                  v-bind="field"
                  placeholder="Sélectionner l'année académique"/>
              </Field>
              <ErrorMessage name="anneeAcademique" class="text-danger"/>
            </div>
          </div>

          <!-- Type de composition (compact, même ligne) -->
          <div class="col-md-4">
            <div class="form-group mb-3">
              <label class="d-block text-black fw-semibold mb-10">
                Type de composition <span class="text-danger">*</span>
              </label>
              <div class="d-flex align-items-center">
                <div class="form-check me-4">
                  <Field name="typeComposition" type="radio" value="QCM" id="qcmRadio" 
                         @change="handleTypeChange('QCM')" class="form-check-input me-2"/>
                  <label class="form-check-label fw-medium" for="qcmRadio">QCM</label>
                </div>
                <div class="form-check">
                  <Field name="typeComposition" type="radio" value="QuestionsReponses" id="qrRadio" 
                         @change="handleTypeChange('QuestionsReponses')" class="form-check-input me-2"/>
                  <label class="form-check-label fw-medium" for="qrRadio">Questions/Réponses</label>
                </div>
              </div>
              <ErrorMessage name="typeComposition" class="text-danger"/>
            </div>
          </div>
        </div>

        <!-- Section Questions QCM - Coins droits + QCM en blanc -->
        <div v-if="typeComposition === 'QCM'" class="row mt-4">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-3 bg-primary">
                <div class="col-md-7 ps-4">
                  <h4 class="fs-4 text-white mb-0">QCM</h4>
                </div>
                <div class="col-md-5 pe-4">
                  <div class="badge bg-light text-primary fs-6 float-end" v-if="selectedQCMQuestions.length > 0">
                    {{ selectedQCMQuestions.length }} question(s) sélectionnée(s)
                  </div>
                </div>
              </div>

              <div class="p-4">
                <div class="form-group mb-15">
                  <label class="d-block text-black fw-semibold mb-10">
                    Sélectionner les questions <span class="text-danger">*</span>
                  </label>
                  <Field name="questions" v-slot="{ field }">
                  <Multiselect
                    :options="questionsOptions"
                    :searchable="true"
                    multiple="true"
                    mode="tags"
                    track-by="label"
                    label="label"
                    v-model="field.value"
                    v-bind="field"
                    @change="onQCMQuestionsChange"
                    placeholder="Sélectionner les questions"
                    :disabled="!selectedMatiere || selectedQCMQuestions.length >= 20"
                  />
                </Field>
                <small v-if="selectedQCMQuestions.length >= 20" class="text-success fw-medium d-block mt-1">
                  <i class="fa fa-check-circle me-1"></i> Limite de 20 questions atteinte
                </small>
                <ErrorMessage name="questions" class="text-danger"/>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Section Questions/Réponses -->
        <div v-if="typeComposition === 'QuestionsReponses'" class="row mt-4">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-primary">
                <div class="col-md-7 ps-3">
                  <h4 class="fs-4 text-white">Ajouter les questions</h4>
                </div>
                <div class="col-md-5 pe-3">
                  <button class="btn btn-success float-end" type="button" @click="addNewQuestion">
                    <i class="fa fa-plus-circle me-1"></i> Ajouter une question
                  </button>
                </div>
              </div>

              <div class="p-4">
                <div v-for="(question, index) in addedQuestions" :key="question.tempId">
                  <div class="row mt-3 align-items-start">

                    <!-- ✅ Barème devant la question -->
                    <div class="col-md-2">
                      <label class="d-block text-black fw-semibold mb-2">
                        Barème <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model.number="question.bareme"
                        type="number"
                        min="0.5"
                        step="0.5"
                        class="form-control shadow-none text-black"
                        placeholder="Ex: 2"
                      />
                    </div>

                    <div class="col-md-8">
                      <label class="d-block text-black fw-semibold mb-2">
                        Question {{ index + 1 }} <span class="text-danger">*</span>
                      </label>
                      <textarea
                        v-model="question.contenu"
                        class="form-control shadow-none text-black"
                        placeholder="Entrer le contenu de la question"
                        rows="3"
                      ></textarea>
                    </div>

                    <div class="col-md-2 d-flex align-items-start pt-4">
                      <button
                        class="btn btn-danger btn-sm"
                        type="button"
                        @click="removeQuestion(index)"
                        v-if="addedQuestions.length > 1"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <hr class="mt-0">
            </div>
          </div>
        </div>

        <!-- Boutons d'action -->
        <div class="row mt-4">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Création...' : 'Ajouter la composition' }}
              </button>
              <router-link
                to="/liste-compositions"
                class="btn btn-danger transition border-0 lh-1 fw-medium"
              >
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i>
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed, nextTick,watch } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { success, error, showModal, hideModal } from "../../utils/utils";
import { useRouter } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";
import Multiselect from '@vueform/multiselect';
import { Composition, Professeur, Niveau, Matiere, Filiere, Question } from "../../models/Composition";
import { useAuthStore } from '@/services/auth';


interface Option {
  value: string | number;
  label: string;
}

interface MatiereOption extends Option {
  coefficient: number;
  matiereId: number;
  matiere: any;
  niveauId: number;
  niveau: any;
  filiereId: number;
}

export default defineComponent({
  name: "AddComposition",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    const authStore = useAuthStore();
    const compositionForm = ref<any>(null);
    const router = useRouter();
    const titre = ref("");
    const typeComposition = ref<"QCM" | "QuestionsReponses">("QCM");
    
    // 🔹 Variables pour gérer le professeur connecté
    const isProfesseurConnecte = computed(() => {
      return !!authStore.user?.professeur?.id;
    });

    const professeurConnecteId = computed(() => {
      return authStore.user?.professeur?.id || null;
    });

    const professeurConnecteNom = computed(() => {
      if (!authStore.user?.professeur) return '';
      const prof = authStore.user.professeur;
      return `${prof.nom || ''} ${prof.prenom || ''}`.trim();
    });



    // Variables pour niveaux/filières/matières
    const niveauxOptions = ref<Option[]>([]);
    const filieresOptions = ref<Option[]>([]);
    const matieresOptions = ref<MatiereOption[]>([]);
    const selectedNiveau = ref<number | null>(null);
    const selectedFiliere = ref<number | null>(null);
    const selectedMatiere = ref<MatiereOption | null>(null);
    const isLoadingMatieres = ref(false);
    const isLoadingFilieres = ref(false);
    
    const professeurAssignments = ref<{
  niveaux: any[];
  filieres: any[];
  matieres: any[];
} | null>(null);

    // Variables professeurs par matière
    const professeursParMatiere = ref<Map<number, Option[]>>(new Map());
    const professeursOptions = ref<Option[]>([]);
    
    const currentMatiereId = computed(() => {
      return selectedMatiere.value?.matiereId ?? null;
    });

    const currentProfesseursOptions = computed(() => {
      return getProfesseursByMatiere(currentMatiereId.value);
    });
    
    // Autres variables
    const anneesAcademiquesOptions = ref<any[]>([]);
    const questionsOptions = ref<any[]>([]);
    const addedQuestions = ref<{ contenu: string; reponse?: string; bareme?: number; tempId: number }[]>([]);
    const selectedQCMQuestions = ref<any[]>([]);
    const createdComposition = ref<Composition | null>(null);
    const isSubmitting = ref(false);
    let tempQuestionIdCounter = 1;

    //  Schéma de validation modifié
    const compositionSchema = Yup.object().shape({
      titre: Yup.string().required("Le titre est obligatoire"),
      dateComposition: Yup.date().required("La date de composition est obligatoire"),
      dateFinSoumission: Yup.date().required("La date de fin de soumission est obligatoire"),
      dureeMinutes: Yup.number()
        .typeError("La durée doit être un nombre")
        .positive("La durée doit être supérieure à 0")
        .integer("La durée doit être un nombre entier")
        .required("La durée de la composition est obligatoire"),
      nombreEssais: Yup.number()
        .typeError("Le nombre d'essais doit être un nombre")
        .min(1, "Le nombre d'essais minimum est 1")
        .max(3, "Le nombre d'essais maximum est 3")
        .integer("Le nombre d'essais doit être un nombre entier")
        .required("Le nombre d'essais est obligatoire"),
      professeur: Yup.number().nullable().test(
        'professeur-required',
        'Le professeur est obligatoire',
        function(value) {
          if (isProfesseurConnecte.value) return true;
          return value != null;
        }
      ),
      
      niveau: Yup.number().nullable().required("Le niveau est obligatoire"),
      matiere: Yup.number().nullable().required("La matière est obligatoire"),
      filiere: Yup.number().nullable().required("La filière est obligatoire"),
      anneeAcademique: Yup.number().nullable().required("L'année académique est obligatoire"),
      typeComposition: Yup.string().required("Veuillez sélectionner un type de composition"),
      questions: Yup.array().when("typeComposition", {
      is: "QCM",
      then: (schema) =>
        schema
          .length(20, "Vous devez sélectionner exactement 20 questions pour le QCM")
          .required("Veuillez sélectionner les questions"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
      questionsAdded: Yup.array().when("typeComposition", {
        is: "QuestionsReponses",
        then: (schema) => schema.min(1, "Veuillez ajouter au moins une question").required(),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    });

    const handleInvalidSubmit = ({ errors }: any) => {
      console.error("Soumission invalide, erreurs :", JSON.stringify(errors, null, 2));
      error("Veuillez remplir les champs du formulaire");
    };

    const getCurrentDate = () => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      return `${year}-${month}-${day}`;
    };

   

// Dans onMounted
onMounted(async () => {
  // Attendre que l'utilisateur soit chargé
  if (!authStore.user) {
    await new Promise<void>((resolve) => {
      const stop = watch(
        () => authStore.user,
        (user) => {
          if (user) {
            stop(); // arrêter le watch
            resolve();
          }
        },
        { immediate: true }
      );
    });
  }

  // Maintenant on est 100% sûr du rôle
  if (isProfesseurConnecte.value) {
    await fetchProfesseurAssignments();
  } else {
    await fetchNiveaux();
  }

  await Promise.all([
    fetchProfesseursParMatiere(),
    fetchAnneesAcademiques(),
  ]);

  addNewQuestion();

  nextTick(() => {
    if (isProfesseurConnecte.value && compositionForm.value) {
      compositionForm.value.setFieldValue('professeur', professeurConnecteId.value);
    }
  });
});

    const fetchNiveaux = async () => {
      try {
        const response = await ApiService.get('/all/niveaux?limit=0');
        const niveauxData = response.data.data.data;
        niveauxOptions.value = niveauxData.map((niveau: any) => ({
          value: niveau.id,
          label: niveau.libelle,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des niveaux", err);
        error("Impossible de charger les niveaux");
      }
    };

    const fetchFilieresParNiveau = async (niveauId: number) => {
  if (!niveauId) {
    filieresOptions.value = [];
    return;
  }

  isLoadingFilieres.value = true;
  try {
    if (isProfesseurConnecte.value && professeurAssignments.value) {
      // 🔹 PROFESSEUR : Filtrer selon ses assignations
      const filieresProfesseur = professeurAssignments.value.filieres.filter(
        (f: any) => f.niveauId === niveauId
      );

      filieresOptions.value = filieresProfesseur.map((filiere: any) => ({
        value: filiere.id,
        label: filiere.libelle,
      }));

      if (filieresOptions.value.length === 0) {
        error("Vous n'enseignez dans aucune filière de ce niveau.");
      }
    } else {
      // 🔹 ADMIN : Charger toutes les filières
      const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&limit=0`);
      
      if (response.data.code === 200) {
        const filiereNiveauMatieres = response.data.data.data;
        const filieresUniques = Array.from(new Set(filiereNiveauMatieres.map((fnm: any) => fnm.filiere.id)))
          .map(id => filiereNiveauMatieres.find((fnm: any) => fnm.filiere.id === id).filiere);
        
        filieresOptions.value = filieresUniques.map((filiere: any) => ({
          value: filiere.id,
          label: filiere.libelle,
        }));
      }
    }
  } catch (err: unknown) {
    console.error("Erreur lors de la récupération des filières", err);
    filieresOptions.value = [];
  } finally {
    isLoadingFilieres.value = false;
  }
};


    const fetchAnneesAcademiques = async () => {
      try {
        const response = await ApiService.get('/all/annees?limit=0');
        const anneesData = response.data.data.data;
        anneesAcademiquesOptions.value = anneesData.map((annee: any) => ({
          value: annee.id,
          label: `${annee.libelle}`,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des années académiques", err);
      }
    };

    const fetchQuestions = async (niveauId?: number, filiereId?: number, matiereId?: number) => {
      try {
        let url = '/questionsfiltered?limit=0';
        if (niveauId) url += `&niveauId=${niveauId}`;
        if (filiereId) url += `&filiereId=${filiereId}`;
        if (matiereId) url += `&matiereId=${matiereId}`;

        const response = await ApiService.get(url);
        const questionsData = response.data.data.data;
        console.log("Questions filtrées", questionsData);
        questionsOptions.value = questionsData.map((question: Question) => ({
          value: question.id,
          label: question.contenu,
        }));
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des questions", err);
      }
    };

    const fetchProfesseursParMatiere = async () => {
      try {
        const responseMatieres = await ApiService.get('/filiereNiveauMatieres?limit=0');
        
        if (responseMatieres.data?.code === 200 && Array.isArray(responseMatieres.data.data?.data)) {
          const toutesLesMatieres = responseMatieres.data.data.data;
          
          const matiereIdsSet = new Set<number>();
          toutesLesMatieres.forEach((fnm: any) => {
            if (fnm?.matiere?.id && typeof fnm.matiere.id === 'number' && fnm.matiere.id > 0) {
              matiereIdsSet.add(fnm.matiere.id);
            }
          });
          
          const matiereIds: number[] = Array.from(matiereIdsSet);

          if (matiereIds.length === 0) {
            console.warn('Aucune matière valide trouvée');
            return;
          }

          const professeurPromises = matiereIds.map(async (matiereId: number) => {
            try {
              const responseProfesseurs = await ApiService.get(`/professeurs/matiere/${matiereId}?limit=0`);
              
              if (responseProfesseurs.data?.code === 200 && Array.isArray(responseProfesseurs.data.data?.data)) {
                const professeursData = responseProfesseurs.data.data.data;
                const professeursOptions = professeursData
                  .map((prof: any) => ({
                    value: Number(prof.id),
                    label: `${prof.nom || ''} ${prof.prenom || ''}`.trim(),
                    matiereId: matiereId,
                    email: prof.email,
                    npi: prof.npi
                  }))
                  .filter((prof: any) => prof.value > 0 && prof.label && prof.label !== '');
                
                professeursParMatiere.value.set(matiereId, professeursOptions);
                return { matiereId, success: true, count: professeursOptions.length };
              } else {
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: false, error: 'API response invalid' };
              }
            } catch (err: any) {
              if (err.response?.status === 404) {
                professeursParMatiere.value.set(matiereId, []);
                return { matiereId, success: true, count: 0, empty: true };
              }
              
              professeursParMatiere.value.set(matiereId, []);
              return { matiereId, success: false, error: err.message };
            }
          });

          await Promise.all(professeurPromises);
        }
      } catch (err: unknown) {
        console.error("Erreur lors de la récupération des professeurs par matière", err);
      }
    };

    const fetchProfesseurAssignments = async () => {
  if (!isProfesseurConnecte.value) {
    return; // Si ce n'est pas un professeur, on ne charge rien
  }

  try {
    console.log(' Chargement des assignations du professeur...');
    const response = await ApiService.get('/professeur/assignments');
    
    if (response.data.code === 200) {
      professeurAssignments.value = response.data.data;
      
      // Filtrer les options selon les assignations
      if (professeurAssignments.value) {
        // Filtrer les niveaux
        niveauxOptions.value = professeurAssignments.value.niveaux.map((niveau: any) => ({
          value: niveau.id,
          label: niveau.libelle,
        }));

         if (niveauxOptions.value.length === 0) {
        error("Vous n'avez aucun cours assigné dans l'emploi du temps. Veuillez contacter l'administration.");
      }
      }
    }
  } catch (err: any) {
    console.error("Erreur lors de la récupération des assignations:", err);
    
    // Si le professeur n'a aucun cours assigné, afficher un message
    if (err.response?.status === 200 && err.response?.data?.data) {
      error("Vous n'avez aucun cours assigné dans l'emploi du temps. Veuillez contacter l'administration.");
    }
  }
};


    const getProfesseursByMatiere = (matiereId: number | null | undefined): Option[] => {
      if (!matiereId || typeof matiereId !== 'number' || matiereId <= 0) {
        return [];
      }
      return professeursParMatiere.value.get(matiereId) || [];
    };

const fetchMatieresFiltered = async (niveauId: number, filiereId: number) => {
  if (!niveauId || !filiereId) {
    matieresOptions.value = [];
    return;
  }

  isLoadingMatieres.value = true;
  try {
    if (isProfesseurConnecte.value && professeurAssignments.value) {
      const matieresProfesseur = professeurAssignments.value.matieres.filter(
        (m: any) => m.niveauId === niveauId && m.filiereId === filiereId
      );

      // ✅ Déduplication par matiereId
      const matieresUniques = Array.from(
        new Map(matieresProfesseur.map((m: any) => [m.matiereId, m])).values()
      );

      matieresOptions.value = matieresUniques.map((matiere: any) => ({
        value: matiere.id,
        label: matiere.libelle,
        coefficient: matiere.coefficient,
        matiereId: matiere.matiereId,
        matiere: matiere.matiere,
        niveau: matiere.niveau,
        niveauId: matiere.niveauId,
        filiereId: matiere.filiereId
      }));

      if (matieresOptions.value.length === 0) {
        error("Vous n'enseignez aucune matière dans cette combinaison niveau-filière.");
      }
    } else {
      const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&filiere=${filiereId}&limit=0`);

      if (response.data.code === 200) {
        const filiereNiveauMatieres = response.data.data.data;

        // ✅ Déduplication par matiere.id
        const matieresUniques = Array.from(
          new Map(filiereNiveauMatieres.map((fnm: any) => [fnm.matiere.id, fnm])).values()
        );

        matieresOptions.value = matieresUniques.map((fnm: any) => ({
          value: fnm.id,
          label: `${fnm.matiere.libelle}`,
          coefficient: fnm.coefficient,
          matiereId: fnm.matiere.id,
          matiere: fnm.matiere,
          niveauId: fnm.niveau.id,
          niveau: fnm.niveau,
          filiereId: fnm.filiere.id
        }));
      }
    }
  } catch (err: unknown) {
    console.error("Erreur lors de la récupération des matières", err);
    matieresOptions.value = [];
  } finally {
    isLoadingMatieres.value = false;
  }
};


    const onNiveauChange = async (niveauValue: any) => {
      selectedNiveau.value = typeof niveauValue === 'object' ? niveauValue.value : niveauValue;

      selectedFiliere.value = null;
      selectedMatiere.value = null;
      filieresOptions.value = [];
      matieresOptions.value = [];
      await compositionForm.value?.setFieldValue('filiere', null);
      await compositionForm.value?.setFieldValue('matiere', null);
      
      if (!isProfesseurConnecte.value) {
        await compositionForm.value?.setFieldValue('professeur', null);
      }

      if (selectedNiveau.value) {
        await fetchFilieresParNiveau(selectedNiveau.value);
      }
    };

    const onFiliereChange = async (filiereValue: any) => {
      selectedFiliere.value = filiereValue;
      
      selectedMatiere.value = null;
      matieresOptions.value = [];
      await compositionForm.value?.setFieldValue('matiere', null);
      
      if (!isProfesseurConnecte.value) {
        await compositionForm.value?.setFieldValue('professeur', null);
      }

      if (filiereValue && selectedNiveau.value) {
        await fetchMatieresFiltered(selectedNiveau.value, filiereValue);
      }
    };

   const onMatiereChange = async (matiereValue: any) => {
    const selectedOption = matieresOptions.value.find(opt => opt.value === matiereValue);
    selectedMatiere.value = selectedOption || null;

    if (!isProfesseurConnecte.value) {
      await compositionForm.value?.setFieldValue('professeur', null);
    }

    questionsOptions.value = [];
    selectedQCMQuestions.value = [];
    await compositionForm.value?.setFieldValue('questions', []);

    if (selectedOption) {
      await fetchQuestions(
        selectedOption.niveauId,
        selectedOption.filiereId,
        selectedOption.matiereId
      );
    }
  };

    const handleTypeChange = async (type: "QCM" | "QuestionsReponses") => {
      typeComposition.value = type;
      if (type === "QCM") {
        addedQuestions.value = [];
        selectedQCMQuestions.value = [];
        await compositionForm.value?.setFieldValue("questionsAdded", []);
        await compositionForm.value?.setFieldValue("questions", []);
      } else {
        selectedQCMQuestions.value = [];
        await compositionForm.value?.setFieldValue("questions", []);
        if (addedQuestions.value.length === 0) {
          addNewQuestion();
        }
      }
    };

    // const onQCMQuestionsChange = (selectedQuestions: any[]) => {
    //   selectedQCMQuestions.value = selectedQuestions || [];
    // };

    const onQCMQuestionsChange = (selectedQuestions: any[]) => {
      if (selectedQuestions.length > 20) {
        error("Vous ne pouvez pas sélectionner plus de 20 questions pour un QCM.");
        const limitées = selectedQuestions.slice(0, 20);
        selectedQCMQuestions.value = limitées;
        compositionForm.value?.setFieldValue('questions', limitées);
        return;
      }
      selectedQCMQuestions.value = selectedQuestions || [];
    };

    const addNewQuestion = async () => {
      try {
        addedQuestions.value.push({
          contenu: "",
          reponse: "",
          bareme: 1, // valeur par défaut initiale
          tempId: tempQuestionIdCounter++,
        });
        await compositionForm.value?.setFieldValue('questionsAdded', addedQuestions.value);
      } catch (err: unknown) {
        console.error("Erreur lors de l'ajout de la question:", err);
        error("Erreur lors de l'ajout de la question");
      }
    };

    const removeQuestion = async (index: number) => {
      addedQuestions.value.splice(index, 1);
      await compositionForm.value?.setFieldValue('questionsAdded', addedQuestions.value);
    };

    const addComposition = async (values: any, { resetForm }: any) => { 
      isSubmitting.value = true;

      try {
        if (!values.titre?.trim()) throw new Error("Veuillez renseigner un titre");
        if (!values.dateComposition) throw new Error("Veuillez sélectionner une date");
        if (!values.dateFinSoumission) throw new Error("Veuillez sélectionner une date");
        if (!values.anneeAcademique) throw new Error("Veuillez sélectionner une année académique");
        if (!values.typeComposition) throw new Error("Veuillez sélectionner un type de composition");
        if (!values.matiere) throw new Error("Veuillez sélectionner une matière");
        if (!values.dureeMinutes) throw new Error("Veuillez indiquer la durée de la composition");

        const filiereNiveauMatiereId = values.matiere;

        let professeurId: number;

        if (isProfesseurConnecte.value) {
          professeurId = professeurConnecteId.value!;
          console.log(`Professeur connecté détecté (ID: ${professeurId})`);
        } else {
          if (!values.professeur) {
            throw new Error("Veuillez sélectionner un professeur");
          }
          professeurId = values.professeur;
          console.log(` Professeur sélectionné par admin (ID: ${professeurId})`);
        }

        const payload: any = {
          titre: values.titre,
          dateComposition: values.dateComposition,
          dateFinSoumission: values.dateFinSoumission,
          dureeMinutes: Number(values.dureeMinutes),
          nombreEssais: Number(values.nombreEssais),
          professeurId: professeurId,
          annee: { id: values.anneeAcademique },
          type: values.typeComposition,
          filiereNiveauMatiere: { id: filiereNiveauMatiereId },
        };

        if (values.typeComposition === "QCM") {
          if (!values.questions || values.questions.length === 0) {
            throw new Error("Veuillez sélectionner au moins une question pour le QCM");
          }

          payload.compoQuestions = values.questions.map((q: any) => ({
            question: { id: q.value || q }
          }));
        } 
        else if (values.typeComposition === "QuestionsReponses") {
          if (!addedQuestions.value || addedQuestions.value.length === 0) {
            throw new Error("Veuillez ajouter au moins une question");
          }

          const validQuestions = addedQuestions.value.filter(q => q.contenu?.trim());

          if (validQuestions.length === 0) {
            throw new Error("Veuillez saisir le contenu d'au moins une question");
          }

          payload.questionsToCreate = validQuestions.map(q => ({
            contenu: q.contenu.trim(),
            type: "QuestionsReponses",
            reponse: q.reponse?.trim() || null,
            bareme: q.bareme
          }));
        }

        console.log("Payload envoyé :", JSON.stringify(payload, null, 2));

        const response = await ApiService.post("/compositions", payload);

        if (response.data.code === 200 || response.data.code === 201) {
          success("Composition ajoutée avec succès !");
          resetForm();

          addedQuestions.value = [];
          selectedQCMQuestions.value = [];
          typeComposition.value = "QCM";
          selectedNiveau.value = null;
          selectedFiliere.value = null;
          selectedMatiere.value = null;
          filieresOptions.value = [];
          matieresOptions.value = [];

          router.push("/liste-compositions");
          return;
        }

        throw new Error(response.data.message || "Erreur lors de la création de la composition");

      } catch (err: any) {
        console.error("Erreur lors de l'ajout de la composition :", err);

        if (err.response?.data?.message) {
          error(err.response.data.message);
        } else {
          error(err.message || "Erreur inconnue");
        }

      } finally {
        isSubmitting.value = false;
      }
    };

    const previewComposition = (id?: number) => {
      if (id) {
        const url = `/preview-composition/${id}`;
        window.open(url, '_blank');
      }
    };

    const closeModal = () => {
      const modalEl = document.getElementById("PreviewOrActiveCompositionModal");
      if (modalEl) {
        hideModal(modalEl);
      }
      router.push({ name: "ListeCompositionPage" });
    };

    return {
      compositionSchema,
      compositionForm,
      titre,
      typeComposition,
      professeursOptions,
      niveauxOptions,
      matieresOptions,
      filieresOptions,
      anneesAcademiquesOptions,
      questionsOptions,
      addedQuestions,
      selectedQCMQuestions,
      isSubmitting,
      selectedNiveau,
      selectedFiliere,
      selectedMatiere,
      isLoadingMatieres,
      isLoadingFilieres,
      currentProfesseursOptions,
      isProfesseurConnecte,
      professeurConnecteId,
      professeurConnecteNom,
      handleInvalidSubmit,
      getCurrentDate,
      handleTypeChange,
      onQCMQuestionsChange,
      addComposition,
      previewComposition,
      closeModal,
      removeQuestion,
      addNewQuestion,
      onNiveauChange,
      onFiliereChange,
      onMatiereChange,
      getProfesseursByMatiere,
      createdComposition,
    };
  },
});
</script>

<style scoped>
@import '@vueform/multiselect/themes/default.css';

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.alert {
  border-radius: 0.5rem;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.text-primary {
  color: #007bff !important;
}

.text-success {
  color: #28a745 !important;
}

.border-primary {
  border-color: #007bff !important;
}

.border-success {
  border-color: #28a745 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.form-check-input.me-2 {
  margin-right: 0.75rem;
}

.badge {
  padding: 0.5em 0.75em;
  font-size: 0.875em;
}

.badge.bg-primary {
  background-color: #007bff !important;
}

.badge.bg-success {
  background-color: #28a745 !important;
}
</style>